'use client';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useT, useI18n } from '../../i18n';
import LocaleSwitcher from '../LocaleSwitcher';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const t = useT();
  const { locale } = useI18n();
  const pathname = usePathname() || '/';
  // URL 形狀鐵律：首頁 locale 前綴（/en/）；子頁（/terms/ 等）上 nav hash 連結要跳返主頁對應 section
  const homeHref = locale === 'zh-HK' ? '/' : `/${locale}/`;
  const onHome = pathname.replace(/\/+$/, '') === (locale === 'zh-HK' ? '' : '/' + locale);
  const withHome = (hash: string) => (onHome ? hash : homeHref + hash);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.arsenal'), href: '#arsenal' },
    { name: t('nav.components'), href: '#components' },
    { name: t('nav.custom'), href: '#custom' },
    { name: t('nav.philosophy'), href: '#philosophy' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-jkd-black/95 backdrop-blur-md border-b border-jkd-gray-200' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Left: Language + Logo */}
          <div className="flex items-center gap-4">
            {/* Language Switcher - Always Visible */}
            <LocaleSwitcher />

            {/* Logo（一按返主頁） */}
            <a href={homeHref} className="flex items-center">
              <span className="text-xl lg:text-2xl font-black tracking-tighter">
                <span className="text-white">JKD</span>
                <span className="text-jkd-gold"> Coding</span>
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={withHome(link.href)}
                className="text-gray-300 hover:text-jkd-gold transition-colors duration-300 text-sm font-medium tracking-wide text-water"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <a href={withHome('#contact')} className="btn-gold text-sm px-5 py-2.5">
              {t('nav.consult')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-white p-2"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-16 lg:top-20 left-0 right-0 bg-jkd-black/98 backdrop-blur-md border-b border-jkd-gray-200 transition-all duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={withHome(link.href)}
              className="block text-gray-300 hover:text-jkd-gold transition-colors duration-300 text-lg font-medium py-2"
              onClick={() => setIsOpen(false)}
            >
              {link.name}
            </a>
          ))}
          <a
            href={withHome('#contact')}
            className="block btn-gold text-center text-sm mt-4"
            onClick={() => setIsOpen(false)}
          >
            {t('nav.consult')}
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
